/******************************************************************************

BabiGO
TopBarActions

Gestion des actions de la barre supérieure.

Fonctionnalités actuellement opérationnelles :

* Notifications
* Compteur de notifications non lues
* Ouverture / fermeture du Dropdown
* Fermeture avec clic extérieur
* Fermeture avec Escape
* Profil

Fonctionnalités préparées :

* Synchronisation
* Langue
* Thème
  ==============================================================================
  */

import {

    useCallback,

    useRef,

    useState,

} from "react";

import {

    FiBell,

    FiGlobe,

    FiMoon,

    FiRefreshCw,

    FiUser,

} from "react-icons/fi";

import useNotifications from "../../../hooks/useNotifications";

import NotificationDropdown from "../../notifications/NotificationDropdown";

import ProfileDropdown from "../../profile/ProfileDropdown";

import "./topbar.css";

export default function TopBarActions() {

    /* ======================================================================
    NOTIFICATIONS
    ====================================================================== */

    const [

        notificationsOpen,

        setNotificationsOpen,

    ] = useState(false);


    const notificationButtonRef = useRef(null);

    const {

        unreadCount,

        refresh,

    } = useNotifications();

        const toggleNotifications = useCallback(() => {

        setNotificationsOpen(

            (previous) => !previous

        );

    }, []);

    const closeNotifications = useCallback(() => {

        setNotificationsOpen(false);

    }, []);

    /* ======================================================================
    PROFILE
    ====================================================================== */

    const [
        profileOpen,
        setProfileOpen,
    ] = useState(false);

    const toggleProfile = useCallback(() => {

        setProfileOpen(

            previous => !previous

        );

    }, []);


    const closeProfile = useCallback(() => {

        setProfileOpen(false);

    }, []);

    /* ============================================================================
        ACTUALISATION
    ============================================================================ */

    const [refreshing, setRefreshing] = useState(false);

    const handleRefresh = useCallback(async () => {

    /* ============================================================================
        ACTUALISATION DES DONNÉES

        Pour le moment, le bouton actualise le module Notifications.

        Plus tard, cette fonction pourra devenir le point d'entrée de la
        synchronisation globale de l'application :

        - Notifications
        - Utilisateurs
        - Trajets
        - Véhicules
        - Paiements
        - Dashboard
    ============================================================================ */

        if (refreshing) {

            return;

        }


        try {

            setRefreshing(true);


            await refresh();


            } catch (error) {

                console.error(

                    "[BabiGO] Erreur lors de l'actualisation :",

                    error

                );


            } finally {

                setRefreshing(false);

            }
        
        }, [

            refreshing,

            refresh,
        
        ]);



    /* ======================================================================
    LANGUE
    ====================================================================== */

    const handleLanguage = useCallback(() => {

        /*
        ----------------------------------------------------------------------
        TODO

        Prévoir :

        - français
        - anglais
        - éventuellement d'autres langues

        La préférence devra être persistée localement puis,
        si nécessaire, synchronisée avec le profil utilisateur.
        ----------------------------------------------------------------------
        */

        console.info(

            "[BabiGO] Gestion de la langue : à implémenter."

        );

    }, []);


    /* ======================================================================
    THÈME
    ====================================================================== */

    const handleTheme = useCallback(() => {

        /*
        ----------------------------------------------------------------------
        TODO

        Le système de thème devra gérer :

        - light
        - dark
        - éventuellement system

        À terme, cette logique devrait être centralisée dans
        un ThemeProvider / ThemeContext.

        Ne pas implémenter une logique locale ici pour éviter
        plusieurs sources de vérité.
        ----------------------------------------------------------------------
        */

        console.info(

            "[BabiGO] Gestion du thème : à implémenter."

        );

    }, []);


    /* ======================================================================
    RENDER
    ====================================================================== */

    return (

        <div className="topbar-actions">


            {/* ==============================================================
                ACTUALISATION
            ============================================================== */}

            <button
                className={

                    refreshing

                        ? "topbar-action-button refreshing"

                        : "topbar-action-button"

                }

                title={

                    refreshing

                        ? "Actualisation en cours..."

                        : "Actualiser"

                }

                aria-label={

                    refreshing

                        ? "Actualisation en cours"

                        : "Actualiser"

                }

                type="button"

                onClick={handleRefresh}

                disabled={refreshing}

            >

                <FiRefreshCw />
            
            </button>

            {/* ==============================================================
                NOTIFICATIONS
            ============================================================== */}

            <div className="topbar-notification-wrapper">

                <button

                    ref={notificationButtonRef}

                    className={

                        notificationsOpen

                            ? "topbar-action-button notification-button active"

                            : "topbar-action-button notification-button"

                    }

                    title="Notifications"

                    type="button"

                    aria-label="Notifications"

                    aria-haspopup="true"

                    aria-expanded={notificationsOpen}

                    onClick={toggleNotifications}

                >

                    <FiBell />


                    {

                        unreadCount > 0 && (

                            <span

                                className="notification-badge"

                                aria-label={

                                    `${unreadCount} notification${

                                        unreadCount > 1

                                            ? "s"

                                            : ""

                                    } non lue${

                                        unreadCount > 1

                                            ? "s"

                                            : ""

                                    }`

                                }

                            >

                                {

                                    unreadCount > 99

                                        ? "99+"

                                        : unreadCount

                                }

                            </span>

                        )

                    }

                </button>


                {

                    notificationsOpen && (

                        <NotificationDropdown

                            isOpen={notificationsOpen}

                            onClose={closeNotifications}

                            anchorRef={notificationButtonRef}

                        />

                    )

                }

            </div>


            {/* ==============================================================
                LANGUE
            ============================================================== */}

            <button

                className="topbar-action-button"

                title="Langue"

                type="button"

                onClick={handleLanguage}

            >

                <FiGlobe />

            </button>


            {/* ==============================================================
                THÈME
            ============================================================== */}

            <button

                className="topbar-action-button"

                title="Mode clair / sombre"

                type="button"

                onClick={handleTheme}

            >

                <FiMoon />

            </button>

            {/* ==============================================================
                PROFIL UTILISATEUR
            ============================================================== */}

            <div className="topbar-profile-wrapper">

                <button

                    className={

                        profileOpen

                            ? "topbar-profile-button active"

                            : "topbar-profile-button"

                    }

                    type="button"

                    title="Mon compte"

                    aria-label="Mon compte"

                    aria-haspopup="true"

                    aria-expanded={profileOpen}

                    onClick={toggleProfile}

                >

                    <FiUser />

                </button>


                {

                    profileOpen && (

                        <ProfileDropdown

                            isOpen={profileOpen}

                            onClose={closeProfile}

                        />

                    )

                }

            </div>


        </div>

    );

}
